import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          navigate('/login?error=no_session');
          return;
        }

        // Exchange session_id for user data and session token
        const response = await api.get('/auth/session', {
          headers: { 'X-Session-ID': sessionId },
        });

        const { user, session_token } = response.data;
        
        // Store session token in localStorage as backup
        if (session_token) {
          localStorage.setItem('session_token', session_token);
        }

        setUser(user);
        
        // Redirect based on role
        if (user.role === 'admin') {
          navigate('/admin', { state: { user }, replace: true });
        } else {
          navigate('/portal', { state: { user }, replace: true });
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        
        // Handle 403 - Access denied (no invite code)
        if (error.response?.status === 403) {
          toast.error(error.response?.data?.detail || 'Access denied. You need an invite code to sign up.');
          navigate('/enter', { replace: true });
          return;
        }
        
        // Other errors
        toast.error('Authentication failed. Please try again.');
        navigate('/login?error=auth_failed');
      }
    };

    processAuth();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen bg-[var(--navy-900)] flex items-center justify-center">
      <div className="text-center text-[var(--cream)]">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
        <p className="text-lg">Authenticating...</p>
      </div>
    </div>
  );
}
